/**
 * Decoupled bridge between the plain {@link ApiClient} class and the React
 * {@link AuthContext}. When an *authenticated* request (one that carried a
 * bearer token) returns 401 the token has expired or been revoked; `ApiClient`
 * calls {@link notifySessionExpired}, and the `AuthProvider` — which registered
 * the handler on mount — clears the session and drops back to the login screen.
 *
 * Kept as a tiny module with no React/router imports so `ApiClient` stays free
 * of UI concerns while we still get a single, central place to react to auth
 * failure instead of every caller surfacing a raw "Invalid token" string.
 */

type SessionExpiredHandler = () => void;

let handler: SessionExpiredHandler | null = null;
let expiredNoticePending = false;

/**
 * Register the handler that tears down the session. `AuthProvider` does this
 * once on mount; pass `null` on unmount to deregister.
 */
export function setSessionExpiredHandler(fn: SessionExpiredHandler | null): void {
  handler = fn;
}

/**
 * Signal that an authenticated request failed auth (401). Invokes the registered
 * handler if one exists. Safe to call repeatedly for a burst of parallel 401s —
 * the handler (logout) is idempotent and no-ops once the token is cleared.
 */
export function notifySessionExpired(): void {
  handler?.();
}

/** Raise the "your session expired" notice for the login screen to consume. */
export function markSessionExpiredNotice(): void {
  expiredNoticePending = true;
}

/**
 * Read-and-clear the pending session-expiry notice. `LoginPage` calls this on
 * mount so the message shows exactly once, and only after a real expiry — never
 * on a first, never-signed-in visit.
 */
export function consumeSessionExpiredNotice(): boolean {
  const pending = expiredNoticePending;
  expiredNoticePending = false;
  return pending;
}
